<script setup lang="ts">
import { UpdateUserDocument } from '~/apollo/gql/graphql'
import InputSearch from '~/components/common/form/InputSearch.vue'

import type { FormError } from '#ui/types'
import type { CorpsOption, Option, UnitKerjaOption } from '~/types/form'

type SyncData = {
  id: number
  name: string
  nrp: string
  email: string
  phone: string
  ojsApiKey: string
  role: string
  gender: Option<string>
  angkatan: Option<number>
  pangkat: Option<number>
  corps: CorpsOption
  unit_kerja: UnitKerjaOption
  unit_organisasi: Option<string>
  jabatan: Option<string>
  satker: UnitKerjaOption
  sub_satker: UnitKerjaOption
  unit_kerja_type: number
  unit_organisasi_type: number
  jabatan_type: EJabatanType
  unit_kerja_custom: string
  unit_organisasi_custom: string
  jabatan_custom: string
}

const emit = defineEmits(['close'])
const props = defineProps<{ type: 'user' | 'admin'; isSync?: boolean }>()

const loadingSubmit = ref(false)
const errors = ref<FormError[]>([])
const formRef = ref()

const initialData = () => ({
  id: null,
  name: '',
  nrp: '',
  role: 'user',
  email: '',
  phone: '',
  ojsApiKey: '',
  gender: null,
  angkatan: null,
  pangkat: null,
  corps: null,
  unit_kerja: null,
  unit_organisasi: null,
  jabatan: null,
  satker: null,
  sub_satker: null,
  unit_kerja_type: 1,
  unit_organisasi_type: 1,
  jabatan_type: EJabatanType.FUNGSIONAL,
  unit_kerja_custom: null,
  unit_organisasi_custom: null,
  jabatan_custom: null
})
const before = ref<SyncData>({
  id: null,
  name: '',
  nrp: '',
  role: 'user',
  email: '',
  phone: '',
  ojsApiKey: '',
  gender: null,
  angkatan: null,
  pangkat: null,
  corps: null,
  unit_kerja: null,
  unit_organisasi: null,
  jabatan: null,
  satker: null,
  sub_satker: null,
  unit_kerja_type: 1,
  unit_organisasi_type: 1,
  jabatan_type: EJabatanType.FUNGSIONAL,
  unit_kerja_custom: null,
  unit_organisasi_custom: null,
  jabatan_custom: null
})
const after = ref<SyncData>({
  id: null,
  name: '',
  nrp: '',
  role: 'user',
  email: '',
  phone: '',
  ojsApiKey: '',
  gender: null,
  angkatan: null,
  pangkat: null,
  corps: null,
  unit_kerja: null,
  unit_organisasi: null,
  jabatan: null,
  satker: null,
  sub_satker: null,
  unit_kerja_type: 1,
  unit_organisasi_type: 1,
  jabatan_type: EJabatanType.FUNGSIONAL,
  unit_kerja_custom: null,
  unit_organisasi_custom: null,
  jabatan_custom: null
})
const verifiedFields = reactive({
  name: false,
  gender: false,
  angkatan: false,
  pangkat: false,
  corps: false,
  unit_kerja: false,
  unit_organisasi: false,
  jabatan: false,
  satker: false,
  sub_satker: false
})

const { add: addToast } = useToast()
const { mutate: mutateUpdateUser } = useMutation(UpdateUserDocument)
const { validatePersonel, onSearchInput } = useFormValidation()

const {
  angkatanOptions,
  pangkatOptions,
  corpsOptions,
  unitOrganisasiOptions,

  isTni,
  previewFullName,
  previewFullPosition,

  loadOptions,
  searchUnitKerja,
  searchSatker,
  searchSubsatker,
  searchJabatan,
  searchJabatanKepala,

  loadingOptions,
  loadingUnitKerja,
  loadingSatker,
  loadingSubsatker,
  loadingJabatan,
  loadingJabatanKepala
} = usePersonelOptions(after)

const resetData = () => {
  before.value = initialData()
  after.value = initialData()
}
const validate = (): FormError[] => {
  const state = after.value
  errors.value = []

  validatePersonel(state, errors, isTni.value)

  return errors.value
}
const onSubmit = async () => {
  const errs = await formRef.value?.validate()
  if (errs?.length) {
    // stop submit kalau ada error
    return
  }
  const {
    id,
    name,
    gender,
    angkatan,
    pangkat,
    corps,
    unit_kerja,
    unit_organisasi,
    jabatan,
    satker,
    sub_satker,
    jabatan_type,
    unit_kerja_custom,
    unit_organisasi_custom,
    jabatan_custom
  } = after.value

  try {
    loadingSubmit.value = true

    await mutateUpdateUser({
      ...(props.type === 'admin' ? { id } : {}),
      name,
      gender: gender?.value,
      angkatanId: angkatan?.value,
      pangkatId: pangkat?.value,
      corpsId: corps?.value,
      unitKerjaKode: unit_kerja?.value || null,
      satkerKode: satker?.value || null,
      subSatkerKode: sub_satker?.value || null,
      unitOrganisasiKode: unit_organisasi?.value,
      jabatanKode: jabatan_type === EJabatanType.FUNGSIONAL ? jabatan?.value : null,
      jabatanKepalaKode: jabatan_type === EJabatanType.KEPALA ? jabatan?.value : null,
      unitKerjaCustom: unit_kerja_custom,
      unitOrganisasiCustom: unit_organisasi_custom,
      jabatanCustom: jabatan_custom
    })

    addToast({
      color: 'green',
      title: 'Sukses!',
      description: props.isSync
        ? 'Berhasil memperbarui biodata dari SIMPEG'
        : 'Berhasil memperbarui biodata'
    })
  } catch {
    addToast({
      color: 'red',
      title: 'Gagal!',
      description: 'Terjadi kesalahan saat memperbarui biodata'
    })
  } finally {
    loadingSubmit.value = false
    emit('close')
  }
}

const initSync = (beforePayload: SyncData, afterPayload: SyncData) => {
  resetData()

  before.value = Object.assign({}, before.value, beforePayload)
  after.value = Object.assign({}, before.value, afterPayload)

  if (afterPayload.name) verifiedFields.name = true
  if (afterPayload.gender) verifiedFields.gender = true
  if (afterPayload.angkatan?.value) verifiedFields.angkatan = true
  if (afterPayload.pangkat?.value) verifiedFields.pangkat = true
  if (afterPayload.corps?.value) verifiedFields.corps = true
  if (afterPayload.unit_kerja?.value) verifiedFields.unit_kerja = true
  if (afterPayload.unit_organisasi?.value) verifiedFields.unit_organisasi = true
  if (afterPayload.jabatan?.value || afterPayload.jabatan_custom) verifiedFields.jabatan = true
  if (afterPayload.satker?.value) verifiedFields.satker = true
  if (afterPayload.sub_satker?.value) verifiedFields.sub_satker = true
}
const initEdit = (beforePayload: SyncData) => {
  resetData()

  before.value = Object.assign({}, before.value, beforePayload)
  after.value = Object.assign({}, after.value, beforePayload)
}

defineExpose({ initSync, initEdit })

onMounted(() => {
  loadOptions()
})
</script>

<template>
  <UForm
    ref="formRef"
    :state="before"
    class="space-y-4"
    :validate="validate"
    :validate-on="['input']"
    @submit="onSubmit"
  >
    <div class="flex flex-col gap-1">
      <label>Nama Lengkap (dengan gelar) <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <UInput
            id="name-before"
            v-model="before.name"
            color="gray"
            disabled
            placeholder="-"
            class="w-full"
            type="text"
          />
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="name"
            required
          >
            <UInput
              id="name-after"
              v-model="after.name"
              :disabled="verifiedFields.name"
              :color="verifiedFields.name ? 'green' : 'white'"
              placeholder="Masukkan nama lengkap"
              type="text"
              class="w-full"
              required
            />
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Jenis Kelamin <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <UInputMenu
            id="gender-before"
            v-model="before.gender"
            color="gray"
            disabled
            class="w-full"
            placeholder="-"
            :options="genderOptions"
          />
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="gender"
            required
          >
            <UInputMenu
              id="gender-after"
              v-model="after.gender"
              :disabled="verifiedFields.gender"
              :color="verifiedFields.gender ? 'green' : 'white'"
              class="w-full"
              placeholder="Pilih opsi gender"
              :options="genderOptions"
            />
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Angkatan <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <UInputMenu
            id="angkatan-before"
            v-model="before.angkatan"
            :options="angkatanOptions"
            :loading="loadingOptions"
            trailing
            disabled
            color="gray"
            placeholder="-"
            class="w-full"
          />
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="angkatan"
            required
          >
            <UInputMenu
              id="angkatan-after"
              v-model="after.angkatan"
              :options="angkatanOptions"
              :loading="loadingOptions"
              :disabled="verifiedFields.angkatan"
              :color="verifiedFields.angkatan ? 'green' : 'white'"
              trailing
              placeholder="Pilih opsi angkatan"
              class="w-full"
              @update:query="after.angkatan = null"
              @change="((after.pangkat = null), (after.corps = null))"
              @input="onSearchInput('angkatan', $event)"
            />
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Pangkat <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <UInputMenu
            id="pangkat-before"
            v-model="before.pangkat"
            :options="pangkatOptions"
            :loading="loadingOptions"
            disabled
            color="gray"
            placeholder="-"
            class="w-full"
            trailing
          />
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="pangkat"
            required
          >
            <UInputMenu
              id="pangkat-after"
              v-model="after.pangkat"
              :options="pangkatOptions"
              :loading="loadingOptions"
              :disabled="verifiedFields.pangkat"
              :color="verifiedFields.pangkat ? 'green' : 'white'"
              placeholder="Pilih opsi pangkat"
              class="w-full"
              trailing
              @update:query="after.pangkat = null"
              @input="onSearchInput('pangkat', $event)"
            />
          </UFormGroup>
        </div>
      </div>
    </div>
    <template v-if="isTni">
      <div class="flex flex-col gap-1">
        <label>Corps <span class="text-red-400">*</span></label>
        <div class="flex items-start gap-3">
          <!-- Sebelah kiri -->
          <div class="flex-1">
            <UInputMenu
              id="corps-before"
              v-model="before.corps"
              :options="corpsOptions"
              :loading="loadingOptions"
              trailing
              disabled
              color="gray"
              placeholder="-"
              class="w-full"
            >
              <template #option="{ option }">
                <span class="truncate">{{ option.label }}</span>
                <span class="text-right font-semibold text-gray-400">
                  {{ before.gender?.value === EGender.MALE ? option.abbr : option.abbr_fem }}
                </span>
              </template>
            </UInputMenu>
          </div>

          <!-- Panah -->
          <div class="mt-1 flex items-center justify-center">
            <UIcon
              name="heroicons:arrow-long-right-16-solid"
              class="text-gray-400"
            />
          </div>

          <!-- Sebelah kanan -->
          <div class="flex-1">
            <UFormGroup
              name="corps"
              required
            >
              <UInputMenu
                id="corps-after"
                v-model="after.corps"
                :options="corpsOptions"
                :loading="loadingOptions"
                :disabled="verifiedFields.corps"
                :color="verifiedFields.corps ? 'green' : 'white'"
                trailing
                placeholder="Pilih opsi corps"
                class="w-full"
                @update:query="after.corps = null"
                @input="onSearchInput('corps', $event)"
              >
                <template #option="{ option }">
                  <span class="truncate">{{ option.label }}</span>
                  <span class="text-right font-semibold text-gray-400">
                    {{ after.gender?.value === EGender.MALE ? option.abbr : option.abbr_fem }}
                  </span>
                </template>
              </UInputMenu>
            </UFormGroup>
          </div>
        </div>
      </div>
    </template>

    <div class="flex flex-col gap-1">
      <label>Unit Kerja <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <div class="w-full space-y-3">
            <URadioGroup
              v-model="before.unit_kerja_type"
              name="unit-kerja-type-before"
              :options="unitKerjaTypeOptions"
              disabled
            />
            <InputSearch
              v-if="before.unit_kerja_type === EUnitKerjaType.SIMPEG"
              id="unit-kerja-before"
              v-model="before.unit_kerja"
              :search="searchUnitKerja"
              :loading="loadingUnitKerja"
              trailing
              disabled
              color="gray"
              placeholder="-"
            />
            <UInput
              v-else
              v-model="before.unit_kerja_custom"
              placeholder="-"
              color="gray"
              disabled
              type="text"
            />
          </div>
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="unit_kerja"
            required
            :error="
              formRef?.errors?.find(
                (e: { path: string }) => e.path === 'unit_kerja' || e.path === 'unit_kerja_custom'
              )?.message
            "
          >
            <div class="w-full space-y-3">
              <URadioGroup
                v-model="after.unit_kerja_type"
                name="unit-kerja-type-after"
                :options="unitKerjaTypeOptions"
                :disabled="verifiedFields.unit_kerja"
                @change="
                  ((after.unit_kerja = null),
                  (after.unit_kerja_custom = null),
                  (after.unit_organisasi = null),
                  (after.unit_organisasi_custom = null))
                "
              />
              <InputSearch
                v-if="after.unit_kerja_type === EUnitKerjaType.SIMPEG"
                id="unit-kerja-after"
                v-model="after.unit_kerja"
                :search="searchUnitKerja"
                :loading="loadingUnitKerja"
                :disabled="verifiedFields.unit_kerja"
                :color="verifiedFields.unit_kerja ? 'green' : 'white'"
                trailing
                placeholder="Cari unit kerja..."
                @input="onSearchInput('unit_kerja', $event)"
              />
              <UInput
                v-else
                v-model="after.unit_kerja_custom"
                placeholder="Masukkan nama unit kerja"
                type="text"
              />
            </div>
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Satker</label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <InputSearch
            id="satker-before"
            v-model="before.satker"
            :search="searchSatker"
            :loading="loadingSatker"
            trailing
            disabled
            color="gray"
            placeholder="-"
          />
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="satker"
          >
            <InputSearch
              id="satker-after"
              v-model="after.satker"
              :search="searchSatker"
              :loading="loadingSatker"
              :disabled="verifiedFields.satker"
              :color="verifiedFields.satker ? 'green' : 'white'"
              trailing
              placeholder="Cari satker..."
              @input="onSearchInput('satker', $event)"
            />
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Sub Satker</label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <InputSearch
            id="sub-satker-before"
            v-model="before.sub_satker"
            :search="searchSubsatker"
            :loading="loadingSubsatker"
            trailing
            disabled
            color="gray"
            placeholder="-"
          />
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="sub_satker"
          >
            <InputSearch
              id="sub-satker-after"
              v-model="after.sub_satker"
              :search="searchSubsatker"
              :loading="loadingSubsatker"
              :disabled="verifiedFields.sub_satker"
              :color="verifiedFields.sub_satker ? 'green' : 'white'"
              trailing
              placeholder="Cari sub satker..."
              @input="onSearchInput('sub_satker', $event)"
            />
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Jabatan <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <div class="w-full space-y-3">
            <URadioGroup
              v-model="before.jabatan_type"
              name="jabatan-type-before"
              :options="jabatanTypeOptions"
              disabled
            />
            <InputSearch
              v-if="before.jabatan_type === EJabatanType.KEPALA"
              id="jabatan-kepala-before"
              v-model="before.jabatan"
              :search="searchJabatanKepala"
              :loading="loadingJabatanKepala"
              trailing
              disabled
              color="gray"
              placeholder="-"
            />
            <InputSearch
              v-else-if="before.jabatan_type === EJabatanType.FUNGSIONAL"
              id="jabatan-before"
              v-model="before.jabatan"
              :search="searchJabatan"
              :loading="loadingJabatan"
              trailing
              disabled
              color="gray"
              placeholder="-"
            />
            <UInput
              v-else
              id="jabatan-before-custom"
              v-model="before.jabatan_custom"
              color="gray"
              placeholder="-"
              disabled
              type="text"
            />
          </div>
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="jabatan"
            required
            :error="
              formRef?.errors?.find(
                (e: { path: string }) =>
                  e.path === 'jabatan' || e.path === 'jabatan_kepala' || e.path === 'jabatan_custom'
              )?.message
            "
          >
            <div class="w-full space-y-3">
              <URadioGroup
                v-model="after.jabatan_type"
                name="jabatan-type-after"
                :options="jabatanTypeOptions"
                :disabled="verifiedFields.jabatan"
                @change="((after.jabatan = null), (after.jabatan_custom = null))"
              />
              <InputSearch
                v-if="after.jabatan_type === EJabatanType.KEPALA"
                id="jabatan-kepala-after"
                v-model="after.jabatan"
                :search="searchJabatanKepala"
                :loading="loadingJabatanKepala"
                :disabled="verifiedFields.jabatan"
                :color="verifiedFields.jabatan ? 'green' : 'white'"
                trailing
                placeholder="Cari jabatan kepala..."
                @input="onSearchInput('jabatan_kepala', $event)"
              />
              <InputSearch
                v-else-if="after.jabatan_type === EJabatanType.FUNGSIONAL"
                id="jabatan-kepala-after"
                v-model="after.jabatan"
                :search="searchJabatan"
                :loading="loadingJabatan"
                :disabled="verifiedFields.jabatan"
                :color="verifiedFields.jabatan ? 'green' : 'white'"
                trailing
                placeholder="Cari jabatan kepala..."
                @input="onSearchInput('jabatan', $event)"
              />
              <UInput
                v-else
                id="jabatan-custom-after"
                v-model="after.jabatan_custom"
                placeholder="Masukkan nama jabatan"
                :disabled="verifiedFields.jabatan"
                type="text"
              />
            </div>
          </UFormGroup>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label>Unit Organisasi <span class="text-red-400">*</span></label>
      <div class="flex items-start gap-3">
        <!-- Sebelah kiri -->
        <div class="flex-1">
          <div class="w-full space-y-3">
            <URadioGroup
              v-model="before.unit_organisasi_type"
              :options="unitOrganisasiTypeOptions"
              disabled
            />
            <UInputMenu
              v-if="before.unit_organisasi_type === EUnitKerjaType.SIMPEG"
              id="unit-organisasi-before"
              v-model="before.unit_organisasi"
              :options="unitOrganisasiOptions"
              :loading="loadingOptions"
              trailing
              disabled
              color="gray"
              placeholder="-"
              class="w-full"
            />
            <UInput
              v-else
              v-model="before.unit_organisasi_custom"
              placeholder="-"
              color="gray"
              disabled
              type="text"
            />
          </div>
        </div>

        <!-- Panah -->
        <div class="mt-1 flex items-center justify-center">
          <UIcon
            name="heroicons:arrow-long-right-16-solid"
            class="text-gray-400"
          />
        </div>

        <!-- Sebelah kanan -->
        <div class="flex-1">
          <UFormGroup
            name="unit_organisasi"
            required
            :error="
              formRef?.errors?.find(
                (e: { path: string }) =>
                  e.path === 'unit_organisasi' || e.path === 'unit_organisasi_custom'
              )?.message
            "
          >
            <div class="w-full space-y-3">
              <URadioGroup
                v-model="after.unit_organisasi_type"
                name="unit-organisasi-type-after"
                :options="unitOrganisasiTypeOptions"
                :disabled="
                  (verifiedFields.unit_organisasi &&
                    after.unit_kerja_type === EUnitKerjaType.SIMPEG) ||
                  after.unit_kerja_type !== EUnitKerjaType.CUSTOM
                "
                @change="((after.unit_organisasi = null), (after.unit_organisasi_custom = null))"
              />
              <UInputMenu
                v-if="after.unit_organisasi_type === EUnitKerjaType.SIMPEG"
                id="unit-organisasi-after"
                v-model="after.unit_organisasi"
                :options="unitOrganisasiOptions"
                :loading="loadingOptions"
                :disabled="
                  (verifiedFields.unit_organisasi &&
                    after.unit_kerja_type === EUnitKerjaType.SIMPEG) ||
                  after.unit_kerja_type !== EUnitKerjaType.CUSTOM
                "
                :color="verifiedFields.unit_organisasi ? 'green' : 'white'"
                trailing
                placeholder="Pilih opsi unit organisasi"
                class="w-full"
                @update:query="after.unit_organisasi = null"
                @input="onSearchInput('unit_organisasi', $event)"
              />
              <UInput
                v-else
                v-model="after.unit_organisasi_custom"
                :disabled="after.unit_kerja_type !== EUnitKerjaType.CUSTOM"
                placeholder="Masukkan nama unit organisasi"
                type="text"
              />
            </div>
          </UFormGroup>
        </div>
      </div>
    </div>

    <UAlert
      title="Pratinjau Data Anda"
      :description="previewFullName"
    >
      <template #description>
        <div class="mb-2 mt-4">{{ previewFullName || '-' }}</div>
        <div class="text-gray-500 dark:text-gray-400">{{ previewFullPosition || '-' }}</div>
      </template>
    </UAlert>

    <UDivider class="py-1" />

    <div class="flex justify-end">
      <UButton
        type="submit"
        color="primary"
        :loading="loadingSubmit"
      >
        Simpan
      </UButton>
    </div>
  </UForm>
</template>
